<template>
  <div>
    <Modal
      :open="open"
      :border="false"
      width="small"
      :classes="{ modalContent: 'max-md:!max-h-[500px] max-md:!overflow-y-auto' }"
      :backdrop-close="false"
      @on-close="closeModal"
    >
      <template #body>
        <div>
          <h1 class="text-lg font-medium text-app-gray-900 border-b border-app-gray-200 pb-2 mb-2">
            {{ step === 1 ? "Change Plan" : "We're sorry to see you go" }}
          </h1>

          <Card v-if="step === 1" class="p-4 flex flex-col gap-2 mt-6 mb-2" :shadow="false">
            <h2 class="mb-3 flex items-center justify-between">
              <span class="text-2xl font-bold text-app-gray-900 flex items-center gap-2">
                Web3Auth
                <Badge variant="info" class="w-max uppercase">{{ props.downgradePlan }}</Badge>
              </span>
              <span class="text-lg font-semibold text-app-gray-500">${{ planMonthlyPrice }} USD</span>
            </h2>

            <div class="text-app-gray-500 mb-3">
              Your current plan remains unchanged for now until your billing cycle ends. After that, you'll be downgraded to
              {{ capitalize(props.downgradePlan) }} plan at ${{ planMonthlyPrice }}/month.
            </div>

            <div class="mb-2">
              <div class="text-app-gray-500 font-bold mb-1">By downgrading, you will lose access to the following:</div>
              <ul>
                <li v-for="feature in loseAccessToFeatures" :key="feature">
                  <div class="flex items-center gap-2">
                    <Icon name="x-circle-solid-icon" class="text-app-red-500 w-4 h-4" />
                    <div class="text-app-gray-500">{{ feature }}</div>
                  </div>
                </li>
              </ul>
              <div
                class="mt-3 flex items-start gap-x-3 rounded-lg border border-app-gray-200 bg-app-gray-50 dark:bg-app-dark-surface3 p-3.5 shadow-sm"
                role="note"
              >
                <div class="flex items-center justify-center w-10 h-10 rounded-full bg-app-orange-100 shrink-0">
                  <Icon name="exclamation-circle-solid-icon" class="text-app-orange-600 w-5 h-5" />
                </div>
                <p class="text-sm text-app-gray-900 leading-snug pt-1.5">
                  Please make sure to disable/reset any features that will be gated before the downgrade takes effect.
                </p>
              </div>
            </div>

            <Button
              block
              :disabled="downgrading"
              :loading="downgrading"
              :classes="{ container: 'bg-app-red-500 hover:!bg-app-red-600' }"
              @click="goToNextStep"
            >
              Downgrade to {{ capitalize(props.downgradePlan) }}
            </Button>
          </Card>

          <div v-if="step === 2" class="mt-6">
            <div class="flex flex-col gap-4">
              <div class="text-app-gray-500">Before you downgrade, we’d love to ask a few quick questions to help us improve.</div>
              <Select
                id="downgradeReason"
                v-model="state.downgradeReason"
                label="What is your reason for downgrading?"
                aria-label="'What is your reason for downgrading?"
                placeholder="Select a reason"
                :options="DOWNGRADE_REASONS"
                :match-parents-width="true"
              />
              <TextArea
                v-model="state.downgradeAdditionalFeedback"
                :label="additionalFeedbackLabel"
                :aria-label="additionalFeedbackLabel"
                :classes="{ textAreaInput: '!min-h-40' }"
              />
            </div>

            <div class="mt-2 mb-4">
              <div v-if="downgradeError" class="text-xs text-app-red-500">{{ downgradeError }}</div>
            </div>

            <div class="flex gap-2">
              <Button variant="secondary" block @click="closeModal">Stay subscribed</Button>

              <Button
                block
                :disabled="v$.$invalid || downgrading"
                :loading="downgrading"
                :classes="{ container: 'bg-app-red-500 hover:!bg-app-red-600' }"
                @click="downgradeSubscription"
              >
                Confirm Downgrade
              </Button>
            </div>
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { Badge } from "@toruslabs/vue-components/Badge";
import { Icon } from "@toruslabs/vue-components/Icon";
import { Modal } from "@toruslabs/vue-components/Modal";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import { computed, reactive, ref } from "vue";

import { Button } from "@/components/Button";
import { Card } from "@/components/Card";
import { Select } from "@/components/Select";
import { TextArea } from "@/components/TextArea";
import { orgAnalytics, ORGANIZATION_ANALYTICS_EVENTS } from "@/helpers/analytics";
import { trackBillingPlanChangePage } from "@/helpers/gtmSubscriptionVirtualPages";
import log from "@/helpers/log";
import { billingModule, userModule } from "@/store";
import { SUBSCRIPTION_PLAN, SUBSCRIPTION_PLAN_INFO, TOAST_TYPES } from "@/utils/enums";
import { capitalize } from "@/utils/string";

const lackingImportantFeature = "Web3Auth lacks an important feature(s) that we need";
const challengesWithProduct = "We have experienced challenges with Web3Auth’s product";
const DOWNGRADE_REASONS = [
  "We are winding down our company",
  "We launched a short term project which is now completed",
  "We can no longer afford Web3Auth",
  "We no longer need social login option",
  challengesWithProduct,
  "I’ve found a better service or product",
  lackingImportantFeature,
  "Other",
].map((reason) => ({ name: reason, value: reason }));

const props = withDefaults(
  defineProps<{
    selectedTeamId: number;
    open: boolean;
    currentPlan: string;
    downgradePlan: string;
  }>(),
  {
    currentPlan: "",
    downgradePlan: "",
  }
);

const step = ref(1);
const downgrading = ref(false);
const downgradeError = ref("");

const state = reactive({
  downgradeReason: "",
  downgradeAdditionalFeedback: "",
});

const emit = defineEmits<{
  (e: "close"): void;
}>();

const rules = {
  downgradeReason: {
    required,
  },
  downgradeAdditionalFeedback: {
    required: helpers.withMessage("Please provide additional feedback", (value: string) => {
      if (state.downgradeReason === lackingImportantFeature || state.downgradeReason === challengesWithProduct) {
        return Boolean(value);
      }
      return true;
    }),
  },
};
const v$ = useVuelidate(rules, state);

const goToNextStep = () => {
  step.value += 1;
};

const reset = () => {
  step.value = 1;
  state.downgradeReason = "";
  state.downgradeAdditionalFeedback = "";
};

const closeModal = () => {
  reset();
  emit("close");
};

const planMonthlyPrice = computed(() => {
  return SUBSCRIPTION_PLAN_INFO[props.downgradePlan]?.monthlyPrice ?? "";
});

const additionalFeedbackLabel = computed(() => {
  if (state.downgradeReason === lackingImportantFeature) {
    return "Can you share more about what features you need?";
  }
  if (state.downgradeReason === challengesWithProduct) {
    return "Can you share more about what challenges you faced?";
  }
  return "Is there something we could have done better?";
});

const loseAccessToFeatures = computed(() => {
  // growth -> base
  if (props.currentPlan === SUBSCRIPTION_PLAN.growth && props.downgradePlan === SUBSCRIPTION_PLAN.base) {
    return ["Third-party Authentication Support", "Free 3,000 Monthly Active Wallets (MAWs)", "Whitelabeling"];
  }

  // scale -> base
  if (props.currentPlan === SUBSCRIPTION_PLAN.scale && props.downgradePlan === SUBSCRIPTION_PLAN.base) {
    return ["Free 10,000 Monthly Active Wallets (MAWs)", "Pre-generated Wallet", "Third-party Authentication Support"];
  }

  // scale -> growth
  if (props.currentPlan === SUBSCRIPTION_PLAN.scale && props.downgradePlan === SUBSCRIPTION_PLAN.growth) {
    return ["Wallet UI", "Pre-generated Wallet", "Free 10,000 Monthly Active Wallets (MAWs)"];
  }

  return [];
});

const downgradeSubscription = async () => {
  // analytics
  const trackData = {
    is_upgrade: false,
    old_plan: props.currentPlan,
    new_plan: props.downgradePlan,
    reason: state.downgradeReason,
    additional_feedback: state.downgradeAdditionalFeedback,
  };

  try {
    downgradeError.value = "";
    downgrading.value = true;
    await billingModule.downgradeSubscription({
      teamId: props.selectedTeamId,
      plan: props.downgradePlan,
    });
    userModule.setToastMessage({ message: "Change of subscription plan successful.", type: TOAST_TYPES.SUCCESS });

    // analytics
    orgAnalytics.track(ORGANIZATION_ANALYTICS_EVENTS.PLAN_CHANGE_COMPLETED, trackData);
    trackBillingPlanChangePage("downgradeSuccess");
    closeModal();
  } catch (error) {
    log.error(error);
    downgradeError.value = (error as Error).message;

    // analytics
    orgAnalytics.track(ORGANIZATION_ANALYTICS_EVENTS.PLAN_CHANGE_FAILED, {
      ...trackData,
      error_message: (error as Error).message,
    });
  } finally {
    downgrading.value = false;
  }
};
</script>
